/**
 * @engine/physics2d - Spatial Hash Broad-Phase
 *
 * O(1) insertion, O(1) query for uniformly distributed objects.
 * Uses modular arithmetic for infinite grid without memory limits.
 *
 * Designed to be portable to GPU compute via SpatialHashCellGPU.
 */

import type { PhysVec2 } from '../types';

/** Broad-phase pair candidate */
export interface BroadPhasePair {
  indexA: number;
  indexB: number;
}

/** AABB for insertion */
export interface AABB {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Spatial hash grid for 2D broad-phase collision detection.
 *
 * Each collider is inserted into all cells its AABB overlaps.
 * Query returns all unique pairs of overlapping AABBs.
 */
export class SpatialHash {
  private _cellSize: number;
  private _invCellSize: number;
  private _cells: Map<number, number[]> = new Map();
  private _objectCells: Map<number, number[]> = new Map(); // objectIndex → cellKeys

  /** Number of hash table buckets (prime for good distribution) */
  private static readonly TABLE_SIZE = 6151;

  constructor(cellSize: number = 2.0) {
    if (cellSize <= 0) throw new RangeError('SpatialHash cellSize must be > 0');
    this._cellSize = cellSize;
    this._invCellSize = 1 / cellSize;
  }

  get cellSize(): number { return this._cellSize; }

  /**
   * Clear all entries. Call at start of each physics step.
   */
  clear(): void {
    this._cells.clear();
    this._objectCells.clear();
  }

  /**
   * Insert an object's AABB into the grid.
   * @param index - Unique object index (collider index)
   * @param aabb - World-space AABB
   */
  insert(index: number, aabb: AABB): void {
    const x0 = Math.floor(aabb.minX * this._invCellSize);
    const y0 = Math.floor(aabb.minY * this._invCellSize);
    const x1 = Math.floor(aabb.maxX * this._invCellSize);
    const y1 = Math.floor(aabb.maxY * this._invCellSize);

    const cellKeys: number[] = [];

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const key = SpatialHash._hash(x, y);
        let cell = this._cells.get(key);
        if (!cell) {
          cell = [];
          this._cells.set(key, cell);
        }
        cell.push(index);
        cellKeys.push(key);
      }
    }

    this._objectCells.set(index, cellKeys);
  }

  /**
   * Find all unique colliding pairs.
   * Returns deduplicated pairs where indexA < indexB.
   */
  queryPairs(): BroadPhasePair[] {
    const pairs: BroadPhasePair[] = [];
    const seen = new Set<number>(); // packed pair key for dedup

    for (const cell of this._cells.values()) {
      const len = cell.length;
      for (let i = 0; i < len; i++) {
        for (let j = i + 1; j < len; j++) {
          const a = cell[i];
          const b = cell[j];
          const lo = a < b ? a : b;
          const hi = a < b ? b : a;
          const pairKey = lo * 0x100000 + hi; // pack into single number
          if (!seen.has(pairKey)) {
            seen.add(pairKey);
            pairs.push({ indexA: lo, indexB: hi });
          }
        }
      }
    }

    return pairs;
  }

  /**
   * Query all objects overlapping a given AABB.
   * @returns Unique set of object indices
   */
  queryAABB(aabb: AABB): number[] {
    const x0 = Math.floor(aabb.minX * this._invCellSize);
    const y0 = Math.floor(aabb.minY * this._invCellSize);
    const x1 = Math.floor(aabb.maxX * this._invCellSize);
    const y1 = Math.floor(aabb.maxY * this._invCellSize);

    const result = new Set<number>();

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const key = SpatialHash._hash(x, y);
        const cell = this._cells.get(key);
        if (cell) {
          for (const idx of cell) {
            result.add(idx);
          }
        }
      }
    }

    return Array.from(result);
  }

  /**
   * Query all objects within range of a point.
   */
  queryPoint(px: number, py: number): number[] {
    const key = SpatialHash._hash(
      Math.floor(px * this._invCellSize),
      Math.floor(py * this._invCellSize),
    );
    return this._cells.get(key) ?? [];
  }

  /**
   * Ray query — returns all cells along a ray segment.
   */
  queryRay(
    origin: PhysVec2,
    direction: PhysVec2,
    maxDistance: number,
  ): number[] {
    // DDA ray traversal through spatial hash
    const ix = this._invCellSize;
    let cellX = Math.floor(origin.x * ix);
    let cellY = Math.floor(origin.y * ix);

    const stepX = direction.x >= 0 ? 1 : -1;
    const stepY = direction.y >= 0 ? 1 : -1;

    const tDeltaX = direction.x !== 0 ? Math.abs(this._cellSize / direction.x) : Infinity;
    const tDeltaY = direction.y !== 0 ? Math.abs(this._cellSize / direction.y) : Infinity;

    const nextBoundX = (cellX + (stepX > 0 ? 1 : 0)) * this._cellSize;
    const nextBoundY = (cellY + (stepY > 0 ? 1 : 0)) * this._cellSize;

    let tMaxX = direction.x !== 0 ? (nextBoundX - origin.x) / direction.x : Infinity;
    let tMaxY = direction.y !== 0 ? (nextBoundY - origin.y) / direction.y : Infinity;

    const result = new Set<number>();
    let t = 0;

    while (t < maxDistance) {
      const key = SpatialHash._hash(cellX, cellY);
      const cell = this._cells.get(key);
      if (cell) {
        for (const idx of cell) result.add(idx);
      }

      if (tMaxX < tMaxY) {
        t = tMaxX;
        tMaxX += tDeltaX;
        cellX += stepX;
      } else {
        t = tMaxY;
        tMaxY += tDeltaY;
        cellY += stepY;
      }
    }

    return Array.from(result);
  }

  /**
   * Hash function: maps (x,y) cell coords to bucket index.
   * Uses large primes for good distribution.
   */
  private static _hash(x: number, y: number): number {
    const h = (x * 92837111) ^ (y * 689287499);
    return ((h % SpatialHash.TABLE_SIZE) + SpatialHash.TABLE_SIZE) % SpatialHash.TABLE_SIZE;
  }
}
